import _ from "lodash";

export default async function getAsyncCompetitorsSlidersData(
  competitorScenarioData,
  historicalVolumeByCompany,
  competitorScenarioEstimations,
  selectedScenarioSlice,
  competitorsSlice,
  categoriesSlice,
  companiesSlice,
  brandsSlice,
  historicalVolumeByBrand
) {
  const competitorsCompaniesByCategory = {};

  const competitorEstimationsByCategoryCompany = _.groupBy(
    competitorScenarioEstimations,
    (estimation) => {
      return `${estimation.product.category_id}-${estimation.product.company_id}`;
    }
  );

  const competitorEstimationsByCategoryCompanyBrand = _.groupBy(
    competitorScenarioEstimations,
    (estimation) => {
      return `${estimation.product.category_id}-${estimation.product.company_id}-${estimation.product.brand_id}`;
    }
  );

  const avgPriceByCategoryCompany = {};
  _.map(competitorEstimationsByCategoryCompany, (groupedEstimations, key) => {
    const weightSummatory = _.sumBy(
      groupedEstimations,
      (estimation) => estimation.price * estimation.units
    );
    const avgPrice = _.round(
      weightSummatory / _.sumBy(groupedEstimations, `units`),
      2
    );
    avgPriceByCategoryCompany[key] = avgPrice;
  });

  const avgPriceByCategoryCompanyBrand = {};
  _.map(
    competitorEstimationsByCategoryCompanyBrand,
    (groupedEstimations, key) => {
      const weightSummatory = _.sumBy(
        groupedEstimations,
        (estimation) => estimation.price * estimation.units
      );
      const avgPrice = _.round(
        weightSummatory / _.sumBy(groupedEstimations, `units`),
        2
      );
      avgPriceByCategoryCompanyBrand[key] = avgPrice;
    }
  );

  const categoriesFromSelectedScenario = new Set();
  _.map(selectedScenarioSlice.estimations, (estimation) => {
    categoriesFromSelectedScenario.add(estimation.product.category_id);
  });

  _.map(competitorsSlice, (competitorProduct) => {
    if (competitorProduct.is_product_client) return;

    // We need to filter competitors that do not exist in the actual scenario
    const categoryNumber = parseInt(competitorProduct.category_id, 10);
    if (!categoriesFromSelectedScenario.has(categoryNumber)) return;

    // It's possible to get combinations of "category_id-company_id"
    // that don't exist in scenario_estimations for competitors,
    // so we need to check and don't show these in sliders.
    const categoryCompanyKey = `${competitorProduct.category_id}-${competitorProduct.company_id}`;
    const companyAvgPrice = avgPriceByCategoryCompany[categoryCompanyKey];
    // It is also possible to get combinations of "category_id-company_id-brand_id"
    // that doesn't exist
    const categoryCompanyBrandKey = `${competitorProduct.category_id}-${competitorProduct.company_id}-${competitorProduct.brand_id}`;
    const brandAvgPrice =
      avgPriceByCategoryCompanyBrand[categoryCompanyBrandKey];
    if (!companyAvgPrice) return;
    if (!brandAvgPrice) return;

    // Create categories
    if (
      competitorsCompaniesByCategory[competitorProduct.category_id] ===
      undefined
    ) {
      const competitorCategoryData = _.find(
        categoriesSlice,
        (category) => category.id === competitorProduct.category_id
      );

      competitorsCompaniesByCategory[competitorProduct.category_id] = {
        ...competitorCategoryData,
        isColapsed: false,
        companies: {},
      };
    }

    // Put companies inside categories
    if (
      !competitorsCompaniesByCategory[competitorProduct.category_id].companies[
        competitorProduct.company_id
      ]
    ) {
      const companyData = _.find(
        companiesSlice,
        (comp) => comp.id === competitorProduct.company_id
      );
      const companyUnits = _.find(
        historicalVolumeByCompany,
        (rankedComp) => rankedComp.company_id === competitorProduct.company_id
      );
      const savedProduct = _.find(
        competitorScenarioData?.defaultSliderData,
        (competitorScenarioItem) =>
          competitorScenarioItem.product_id === competitorProduct.id
      );
      let savedValue = 0;
      let isShowed = false;
      if (savedProduct !== undefined && savedProduct.price_percentage !== 0) {
        isShowed = true;
        savedValue = savedProduct.price_percentage * 100;
        if (savedProduct.price_direction === "decrease") {
          savedValue = -savedValue;
        }
      }

      competitorsCompaniesByCategory[competitorProduct.category_id].companies[
        competitorProduct.company_id
      ] = {
        ...companyData,
        isShowed,
        sliderValue: savedValue,
        price: companyAvgPrice,
        categoryId: competitorProduct.category_id,
        products: [],
        brands: {},
        units: companyUnits?.sum_units || 0,
      };
    }
    competitorsCompaniesByCategory[competitorProduct.category_id].companies[
      competitorProduct.company_id
    ].products.push(competitorProduct);

    // Create brands inside company
    if (
      !competitorsCompaniesByCategory[competitorProduct.category_id].companies[
        competitorProduct.company_id
      ].brands[competitorProduct.brand_id]
    ) {
      const brandData = _.find(
        brandsSlice,
        (brand) => brand.id === competitorProduct.brand_id
      );

      const brandUnits = _.find(
        historicalVolumeByBrand,
        (rankedBrand) => rankedBrand.brand_id === competitorProduct.brand_id
      );

      const savedProduct = _.find(
        competitorScenarioData?.defaultSliderData,
        (competitorScenarioItem) =>
          competitorScenarioItem.product_id === competitorProduct.id
      );

      let savedValue = 0;
      let isShowed = false;
      if (savedProduct !== undefined && savedProduct.price_percentage !== 0) {
        isShowed = true;
        savedValue = savedProduct.price_percentage * 100;
        if (savedProduct.price_direction === "decrease") {
          savedValue = -savedValue;
        }
      }

      competitorsCompaniesByCategory[competitorProduct.category_id].companies[
        competitorProduct.company_id
      ].brands[competitorProduct.brand_id] = {
        ...brandData,
        isShowed,
        sliderValue: savedValue,
        price: brandAvgPrice,
        categoryId: competitorProduct.category_id,
        companyId: competitorProduct.company_id,
        id: competitorProduct.brand_id,
        products: [],
        units: brandUnits?.sum_units || 0,
      };
    }

    competitorsCompaniesByCategory[competitorProduct.category_id].companies[
      competitorProduct.company_id
    ].brands[competitorProduct.brand_id].products.push(competitorProduct);
  });

  // We return competitors in array instead of object to have better management when make changes
  // in sliders, like change row element and sort elements by ranked data or name.

  const competitorsCompaniesByCategoryArray = _.reduce(
    competitorsCompaniesByCategory,
    (prev, curr) => {
      const newCompetitor = curr;
      newCompetitor.companies = _.orderBy(curr.companies, "units", "desc");
      if (newCompetitor.companies[0]) {
        newCompetitor.companies[0].isShowed = true;
      }
      if (newCompetitor.companies[1]) {
        newCompetitor.companies[1].isShowed = true;
      }
      if (newCompetitor.companies[2]) {
        newCompetitor.companies[2].isShowed = true;
      }

      newCompetitor.companies = _.reduce(
        newCompetitor.companies,
        (prevComp, currComp) => {
          const newComp = currComp;
          newComp.brands = _.orderBy(currComp.brands, "units", "desc");
          if (newComp.brands[0]) {
            newComp.brands[0].isShowed = true;
          }
          if (newComp.brands[1]) {
            newComp.brands[1].isShowed = true;
          }
          if (newComp.brands[2]) {
            newComp.brands[2].isShowed = true;
          }
          prevComp.push(newComp);
          return prevComp;
        },
        []
      );
      prev.push(newCompetitor);
      return prev;
    },
    []
  );
  return competitorsCompaniesByCategoryArray;
}
